import { useCallback, useEffect, useState } from "react";
import { useNotify, usePermissions } from "react-admin";
import permissions from "app/permissions";
import dataProvider from "server/dataProvider";
import { checkPermission } from "server/security/utils";
import { publicationRequests } from "api/publicationApi";

export const PROFILE_IDENTIFIERS = [
  { source: "orcid", label: "ORCID", url: publicationRequests.publicationProfileOrcid() },
  { source: "scopusId", label: "Scopus Author ID", url: publicationRequests.publicationProfileScopusId() },
  {
    source: "webOfScienceId",
    label: "Web of Science ResearcherID",
    url: publicationRequests.publicationProfileWebOfScienceId(),
  },
];

const EMPTY_PROFILE = { authorNames: [], orcid: null, scopusId: null, webOfScienceId: null };

// The import matches publications by ORCID and author name, so both are required.
export const isProfileReadyForImport = profile => profile.authorNames.length > 0 && !!profile.orcid;

// Loaded once at page level — the profile section displays it and the import button is gated by it.
// The profile belongs to the logged-in employee; every endpoint resolves them from the session.
export const usePublicationProfile = () => {
  const notify = useNotify();
  const { permissions: userPermissions } = usePermissions();
  const canPreview = checkPermission(userPermissions, permissions.canPreviewScientificPublications);
  const [profile, setProfile] = useState(EMPTY_PROFILE);

  const fetchProfile = useCallback(async () => {
    try {
      const [authorNames, ...identifiers] = await Promise.all([
        dataProvider.customRequest(publicationRequests.publicationProfileAuthorNames(), null, "GET"),
        ...PROFILE_IDENTIFIERS.map(({ url }) => dataProvider.customRequest(url, null, "GET")),
      ]);

      setProfile({
        authorNames: authorNames.data ?? [],
        ...Object.fromEntries(PROFILE_IDENTIFIERS.map(({ source }, i) => [source, identifiers[i].data || null])),
      });
    } catch {
      // An empty profile would invite the user to re-enter data that already exists, so the failure must be visible.
      notify("Неуспешно зареждане на научния профил. Опитайте да презаредите страницата", { type: "error" });
    }
  }, [notify]);

  useEffect(() => {
    if (!canPreview) return;
    fetchProfile();
  }, [canPreview, fetchProfile]);

  return { profile, fetchProfile };
};
